import api from "../api";

const auditLogService = {
  async getAllEvents(params = {}) {
    try {
      const res = await api.post("/audits/audit-log", params);
      return res.data;
    } catch (error) {
      console.error("Error fetching audit log events:", error);
      // Fallback to mock data if API fails
      return this.getMockAuditLogData(params);
    }
  },

  async getAuditLogFilters() {
    try {
      const res = await api.get("/audits/audit-log/filters");
      return res.data;
    } catch (error) {
      console.error("Error fetching audit log filters:", error);
      // Fallback to mock data if API fails
      return this.getMockFilters();
    }
  },

  // Mock data methods
  getMockAuditLogData(params = {}) {
    const mockData = this.generateMockAuditLogData();

    // Apply filters if provided
    let filteredData = mockData;

    if (params.user_id !== undefined && params.user_id !== null) {
      if (params.user_id === 0) {
        filteredData = filteredData.filter((item) => !item.user);
      } else {
        filteredData = filteredData.filter(
          (item) => item.user && item.user.id === params.user_id
        );
      }
    }

    if (params.object_num !== undefined && params.object_num !== null) {
      filteredData = filteredData.filter(
        (item) => item.object_num === params.object_num
      );
    }

    if (params.object_id) {
      filteredData = filteredData.filter(
        (item) => item.object_id === parseInt(params.object_id)
      );
    }

    if (params.event_name) {
      filteredData = filteredData.filter(
        (item) => item.event === params.event_name
      );
    }

    if (params.date_from) {
      const fromDate = new Date(params.date_from);
      filteredData = filteredData.filter(
        (item) => new Date(item.created_at) >= fromDate
      );
    }

    if (params.date_to) {
      const toDate = new Date(params.date_to);
      filteredData = filteredData.filter(
        (item) => new Date(item.created_at) <= toDate
      );
    }

    // Apply search if provided
    if (params.search && params.search.value) {
      const searchTerm = params.search.value.toLowerCase();
      filteredData = filteredData.filter(
        (item) =>
          (item.user &&
            item.user.fullName &&
            item.user.fullName.toLowerCase().includes(searchTerm)) ||
          (item.event && item.event.toLowerCase().includes(searchTerm)) ||
          (item.auditable_type &&
            item.auditable_type.toLowerCase().includes(searchTerm)) ||
          (item.url && item.url.toLowerCase().includes(searchTerm))
      );
    }

    // Apply pagination
    const page = params.start
      ? Math.floor(params.start / params.length) + 1
      : 1;
    const length = params.length || 30;
    const start = (page - 1) * length;
    const end = start + length;

    const paginatedData = filteredData.slice(start, end);

    return {
      data: paginatedData,
      recordsTotal: filteredData.length,
      recordsFiltered: filteredData.length,
      draw: params.draw || 1,
    };
  },

  generateMockAuditLogData() {
    const events = ["created", "updated", "deleted", "restored"];
    const objectTypes = [
      "User",
      "Property",
      "Guardian",
      "Ticket",
      "Payment",
      "Room",
      "Document",
    ];
    const urls = [
      "/login",
      "/dashboard",
      "/properties",
      "/guardians",
      "/tickets",
      "/payments",
      "/rooms",
    ];
    const userNames = [
      "Aime Test",
      "John Smith",
      "Jane Doe",
      "Mike Johnson",
      "Sarah Wilson",
      "David Brown",
      "Lisa Davis",
      "Tom Miller",
    ];

    const mockData = [];

    for (let i = 1; i <= 150; i++) {
      const event = events[Math.floor(Math.random() * events.length)];
      const objectType =
        objectTypes[Math.floor(Math.random() * objectTypes.length)];
      const url = urls[Math.floor(Math.random() * urls.length)];
      const hasUser = Math.random() > 0.2; // 80% chance of having a user
      const userId = Math.floor(Math.random() * 100) + 1;

      const mockItem = {
        id: i,
        user: hasUser
          ? {
              id: userId,
              fullName: userNames[Math.floor(Math.random() * userNames.length)],
            }
          : null,
        event: event,
        auditable_type: objectType,
        auditable_id: Math.floor(Math.random() * 10000) + 1,
        url: url,
        created_at: this.generateRandomDate(),
        old_values: this.generateOldValues(event, objectType),
        new_values: this.generateNewValues(event, objectType),
        object_num: Math.floor(Math.random() * objectTypes.length),
        object_id: Math.floor(Math.random() * 10000) + 1,
      };

      mockData.push(mockItem);
    }

    // Sort by created_at descending (newest first)
    return mockData.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  },

  generateOldValues(event, objectType) {
    if (event === "created") return null;

    const oldValues = {};
    switch (objectType) {
      case "User":
        oldValues["last_login"] = this.generateRandomDate();
        oldValues["status"] = "inactive";
        break;
      case "Property":
        oldValues["status"] = "vacant";
        oldValues["last_inspection"] = this.generateRandomDate();
        break;
      case "Guardian":
        oldValues["placement_status"] = "pending";
        oldValues["last_contact"] = this.generateRandomDate();
        break;
      default:
        oldValues["updated_at"] = this.generateRandomDate();
    }
    return oldValues;
  },

  generateNewValues(event, objectType) {
    if (event === "deleted") return null;

    const newValues = {};
    switch (objectType) {
      case "User":
        newValues["last_login"] = this.generateRandomDate();
        newValues["status"] = "active";
        break;
      case "Property":
        newValues["status"] = "occupied";
        newValues["last_inspection"] = this.generateRandomDate();
        break;
      case "Guardian":
        newValues["placement_status"] = "active";
        newValues["last_contact"] = this.generateRandomDate();
        break;
      default:
        newValues["updated_at"] = this.generateRandomDate();
    }
    return newValues;
  },

  generateRandomDate() {
    const now = new Date();
    // Generate dates within the last 30 days, with more recent dates being more common
    const daysAgo = Math.floor(Math.random() * 30);
    const hoursAgo = Math.floor(Math.random() * 24);
    const minutesAgo = Math.floor(Math.random() * 60);

    const pastDate = new Date(
      now.getTime() -
        daysAgo * 24 * 60 * 60 * 1000 -
        hoursAgo * 60 * 60 * 1000 -
        minutesAgo * 60 * 1000
    );
    return pastDate.toISOString();
  },

  getMockFilters() {
    return {
      objects: {
        0: "All",
        1: "User",
        2: "Property",
        3: "Guardian",
        4: "Ticket",
        5: "Payment",
        6: "Room",
        7: "Document",
      },
      events: {
        0: "All",
        1: "created",
        2: "updated",
        3: "deleted",
        4: "restored",
      },
    };
  },
};

export default auditLogService;
